import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import { ICustomReportFilter } from '@principle-theorem/principle-core/interfaces';
import {
  CanBeChartedProperty,
  CustomReportFilter,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import { omit } from 'lodash';
import { DatapointSelectorComponent } from '../../../report-builder/datapoint-selector/datapoint-selector.component';
import { EditReportFilterComponent } from '../edit-report-filter/edit-report-filter.component';

export type IEditReportFilterDialogRequest = {
  dataSource: ReportBuilderDataSourceInstance;
  filter?: ICustomReportFilter;
};

export type IEditReportFilterDialogResponse = {
  deleteFilter: boolean;
  filter: ICustomReportFilter;
};

@Component({
  selector: 'pr-edit-report-filter-dialog',
  templateUrl: './edit-report-filter-dialog.component.html',
  styleUrls: ['./edit-report-filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    EditReportFilterComponent,
    DatapointSelectorComponent,
    InformationBoxComponent,
  ],
})
export class EditReportFilterDialogComponent {
  private _dialogRef =
    inject<
      MatDialogRef<
        EditReportFilterDialogComponent,
        IEditReportFilterDialogResponse
      >
    >(MatDialogRef);
  request = inject<IEditReportFilterDialogRequest>(MAT_DIALOG_DATA);
  filterChanges = signal(this.request.filter);
  measures: CanBeChartedProperty[];

  constructor() {
    this.measures = this._getMeasures(this.request.dataSource);
  }

  datapointSelected(selectedOption: string): void {
    const filter = this.filterChanges();
    const updates = { id: selectedOption, values: [] };

    this.filterChanges.set(
      filter
        ? { ...filter, ...updates }
        : CustomReportFilter.init({ ...updates })
    );
  }

  onFilterChange(updates: Partial<ICustomReportFilter>): void {
    const filter = this.filterChanges();

    this.filterChanges.set(
      filter
        ? { ...filter, ...updates }
        : CustomReportFilter.init({
            ...updates,
            id: updates.id ?? '',
            values: updates.values ?? [],
          })
    );
  }

  delete(filter: ICustomReportFilter): void {
    this._dialogRef.close({ deleteFilter: true, filter });
  }

  submit(): void {
    const filter = this.filterChanges();
    if (!filter || !filter.id || !filter.uid) {
      return;
    }
    this._dialogRef.close({
      deleteFilter: false,
      filter: omit(filter, ['isPlaceholder', 'placeholderMessage']),
    });
  }

  private _getMeasures(
    dataSource: ReportBuilderDataSourceInstance
  ): CanBeChartedProperty[] {
    return dataSource
      .allColumns()
      .filter((column) => column.measure.metadata.isFilterable)
      .map((column) => column.measure);
  }
}
